/**
 * AdminSystemTemplatesPage — admin-only browser/editor for the global
 * `system_platforms/{platformId}/post_templates/*` Firestore catalogue (the seed pool that flows
 * into per-author `post_templates/*` overlays at platform-add time).
 *
 * Route: /admin/system-templates (registered in App.jsx, gated by useAuth().isAdmin).
 *
 * Hosts the existing `SystemTemplateBrowserDialog` in `adminMode={true}`,
 * always open. Same filtering UI as the per-author "Browse catalogue" dialog;
 * card editing affordances surface only when adminMode is on.
 *
 * Defense layers (mirroring `/project/:projectId/content-plan`):
 *   1. Menu skip — admin routes never appear in the navigation menu.
 *   2. Route gating — App.jsx wraps the route in `{isAdmin && <Route … />}`.
 *   3. Component-level redirect — this component itself enforces `isAdmin`
 *      and renders an Alert when the route is reached by a non-admin
 *      (e.g. via direct URL).
 */
import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { SystemTemplateBrowserDialog } from '@modules/content-plan/components/SystemTemplateBrowserDialog';

const ADMIN_TESTID = 'admin-system-templates-page';

export default function AdminSystemTemplatesPage(): React.ReactElement {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }} data-testid={`${ADMIN_TESTID}-no-access`}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-testid={ADMIN_TESTID}>
      <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">System Post Templates</Typography>
        <Typography variant="body2" color="text.secondary">
          Global seed catalogue under <code>system_platforms/{'{platformId}'}/post_templates/{'{tplId}'}</code>.
          Templates here auto-seed into per-author <code>post_templates</code> at
          platform-add time and feed the per-author "Browse catalogue" dialog.
        </Typography>
      </Stack>

      {/* Dialog is always open — page acts as the host. onClose is a no-op
          because there's nowhere to "close" to. */}
      <SystemTemplateBrowserDialog
        open
        onClose={() => {
          /* no-op — page surface */
        }}
        adminMode
      />
    </Box>
  );
}
